import { useSuspenseQuery } from '@tanstack/react-query'

import { useMe } from '@/shared/auth/queries'
import { assert, isString } from '@/shared/validation/utils/assert'

import { tenantSessionQueries } from '../tenant-session'
import { useCurrentTenantValue } from '../tenant-store'

import type { Role, TenantFeatures, TenantSKU } from '../types'

/**
 * @returns current tenant ID — asserts string, only safe inside dashboard routes
 * @warning Only call after _dashboard beforeLoad completes. Throws if tenant is undefined.
 * See CLERK/SSO_TOTP_FIX.md for context. Use useMaybeCurrentTenant for contexts
 * that may render before beforeLoad (skeletons, error boundaries).
 */
export const useCurrentTenant = (): string => {
	const tenant = useCurrentTenantValue()
	assert(isString(tenant), 'Tenant is not a string')
	return tenant
}

/**
 * @returns current tenant ID or undefined — safe in any render context
 * @see useCurrentTenant for the asserting variant (preferred in dashboard components)
 */
export const useMaybeCurrentTenant = (): string | undefined => {
	return useCurrentTenantValue()
}

/**
 * @returns current role for active tenant, derived from /me tenant_roles
 */
export const useCurrentRole = (): Role | undefined => {
	const tenant = useCurrentTenant()
	const { data } = useMe()
	return data.tenant_roles[tenant] as Role | undefined
}

/**
 * @returns whether active tenant is the user's owning tenant
 */
export const useIsOwnerTenant = (): boolean => {
	const tenant = useCurrentTenant()
	const { data } = useMe()
	return data.auth_context?.owning_tenant === tenant
}

/**
 * @returns details of the active tenant — name, SKU, feature flags.
 * Lives here rather than next to its query so `tenant-session.ts` stays free of
 * `shared/auth` imports; see the note there.
 */
export const useTenantDetails = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(tenantSessionQueries.details(tenant))
}

/**
 * @returns current SKU for active tenant
 */
export const useCurrentSKU = (): TenantSKU | undefined => {
	const { data } = useTenantDetails()
	return data?.sku_info
}

/**
 * @returns feature flags for active tenant
 */
export const useTenantFeatures = (): TenantFeatures | undefined => {
	const { data } = useTenantDetails()
	return data?.features
}
